/**
 * PageLinkPicker — the `[[` command palette over the workspace page tree.
 *
 * Same cmdk pattern as `SlashMenu` / `MentionList`. Receives the tiptap
 * `SuggestionProps` (already filtered pages), supports keyboard nav.
 */

import { forwardRef, useEffect, useImperativeHandle, useState } from 'react'
import { Command } from 'cmdk'
import type { Page } from '@friday/shared/realtime'
import { useSuggestionPosition } from '@/lib/tiptap/suggestionPopup'

export interface PageLinkPickerRef {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean
}

export interface PageLinkPickerProps {
  items: Page[]
  command: (it: Page) => void
  query?: string
  clientRect?: (() => DOMRect | null) | null
}

export const PageLinkPicker = forwardRef<PageLinkPickerRef, PageLinkPickerProps>(function PageLinkPicker(
  { items, command, query, clientRect },
  ref,
) {
  const [hover, setHover] = useState(0)
  const pos = useSuggestionPosition(clientRect)

  useEffect(() => {
    setHover(0)
  }, [query, items.length])

  useImperativeHandle(ref, () => ({
    onKeyDown({ event }) {
      if (event.key === 'ArrowDown') {
        setHover((h) => (h + 1) % Math.max(items.length, 1))
        return true
      }
      if (event.key === 'ArrowUp') {
        setHover((h) => (h - 1 + items.length) % Math.max(items.length, 1))
        return true
      }
      if (event.key === 'Enter') {
        const item = items[hover]
        if (item) command(item)
        return true
      }
      return false
    },
  }))

  return (
    <div
      className="z-50 w-80 overflow-hidden rounded-md border border-surface-200 bg-white shadow-lg dark:border-surface-700 dark:bg-surface-800"
      style={pos ? { position: 'fixed', top: pos.top, left: pos.left } : { display: 'none' }}
    >
      <Command shouldFilter={false} label="Link a page">
        <Command.List>
          {items.length === 0 && <div className="px-3 py-2 text-xs text-surface-500">No matching pages.</div>}
          {items.map((p, i) => (
            <Command.Item
              key={p.id}
              value={p.title}
              onSelect={() => command(p)}
              onMouseMove={() => setHover(i)}
              className={
                'flex cursor-pointer items-center gap-2 px-3 py-1.5 text-xs ' +
                (i === hover ? 'bg-surface-100 dark:bg-surface-700' : '')
              }
            >
              <span className="text-sm">{p.icon ?? '📄'}</span>
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{p.title}</span>
                <span className="block truncate text-[10px] text-surface-500">/{p.slug}</span>
              </span>
            </Command.Item>
          ))}
        </Command.List>
      </Command>
    </div>
  )
})
